import { useRef, useState, useEffect } from "react";
import { Container, Form, Row, Col, Label } from "reactstrap";
import { ErrorAlert, LoadingSection } from "../../Components/Display";
import classnames from "classnames";
import { SkinnedButton } from "../../Components/Form/Buttons";
import ValidationErrors from "../Errors/ValidationErrors";
import SessionExpiredError from "../Errors/SessionExpiredError";
import { toast } from "react-toastify";
import { TextInput } from "../../Components/Form/Inputs";
import { useCustomForm } from "../../Utilities/UseCustomForm";
import { useApiWorker } from "../../Utilities/CommonHooks";
import * as Yup from "yup";
import { useParams } from "react-router";
import { useHistory } from "react-router-dom";
import {
  Helper,
  basePathName,
  adminGenericError,
} from "../../Utilities/HelperData";
import { useGlobalDispatch, GlobalActionTypes } from "../../Context";
import { Session } from "inspector";

class ResetPasswordFormValues {
  newPassword: string = "";
  confirmNewPassword: string = "";

  constructor() {}
}

const validationSchema = Yup.object({
  newPassword: Yup.string()
    .min(
      7,
      "New password must be between 7 and 100 characters with at least 1 uppercase, 1 lowercase and 1 number"
    )
    .max(
      100,
      "New password must be between 7 and 100 characters with at least 1 uppercase, 1 lowercase and 1 number"
    )
    .required(""),
  confirmNewPassword: Yup.string().test(
    "validations",
    "The passwords do not match",
    function (value) {
      return value === this.parent.newPassword;
    }
  ),
});

export const ResetPassword = () => {
  const history = useHistory();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [serverErrors, setServerErrors] = useState<any[] | null>(null);
  const [sessionExpired, setSessionExpired] = useState<boolean>(false);
  const pageElementRef = useRef<null | HTMLDivElement>(null);
  const apiWorker = useApiWorker();
  const { id } = useParams<{ id: string }>();
  const initialValues = new ResetPasswordFormValues();
  const [errorMessageIsVisible, setErrorMessageIsVisible] =
    useState<boolean>(false);
  const dispatch = useGlobalDispatch();

  useEffect(() => {
    dispatch({
      type: GlobalActionTypes.SetInitialGetRequestRanOk,
      payload: true,
    });
  }, [dispatch]);

  const handleServerErrors = (errors: any): boolean => {
    if (errors?.status === 401) {
      setSessionExpired(true);
      window.scroll(0, 0);
      setErrorMessageIsVisible(false);
      return true;
    } else if (errors?.status === 500) {
      setServerErrors([adminGenericError]);
      setErrorMessageIsVisible(false);
      return true;
    }
    return false;
  };

  const {
    values,
    formErrors,
    touched,
    handleChange,
    handleBlur,
    handleSubmit,
    handleChangeInDropDown,
    handleYesNoRadioChange,
    radioButtonChange,
    setValues,
    setFormErrors,
  } = useCustomForm({
    initialValues,
    validationSchema,
    onSubmit: async (values: any) => {
      const noErrors = Object.keys(values.errors || {}).length === 0;
      if (!noErrors) {
        setErrorMessageIsVisible(true);
        setServerErrors(null);
        return;
      }

      setErrorMessageIsVisible(false);
      setIsLoading(true);
      let success = true;
      const result = await apiWorker
        .put<ResetPasswordFormValues>(
          `${basePathName}/api/provideractions/resetpassword/`,
          { ...values.values, id: id },
          {}
        )
        .catch((errors: any) => {
          setIsLoading(false);
          if (handleServerErrors(errors)) {
            success = false;
            return;
          }

          if (
            errors &&
            errors.isAxiosError === undefined &&
            Object.keys(errors).length &&
            Object.keys(errors).filter((x) => x !== "").length
          ) {
            setErrorMessageIsVisible(true);
          }

          errors = Helper.cleanUpErrorMessages(errors);
          setFormErrors(errors);

          let err: any = Helper.convertNonFieldErrorsToArray(errors);
          if (err && Array.isArray(err) && err.length) {
            setServerErrors(err);
          } else {
            setServerErrors([]);
          }
        });

      if (!success) return;

      if (result) {
        setServerErrors(null);
        toast.success("The new password has been saved");
        history.goBack();
      }
      setIsLoading(false);
    },
  });

  const goBack = async (event: any) => {
    event.preventDefault();
    history.goBack();
  };

  return (
    <Container fluid={true} className="mt-4 position-relative">
      <LoadingSection
        isLoading={isLoading}
        height={pageElementRef.current?.clientHeight}
        width={pageElementRef.current?.clientWidth}
      >
        <div ref={pageElementRef}>
          <Form className="mt-2 ml-5 mr-5" onSubmit={handleSubmit}>
            <Row>
              <Col className="px-0">
                {errorMessageIsVisible && (
                  <ErrorAlert message="Please update the highlighted fields before saving"></ErrorAlert>
                )}
                <ValidationErrors
                  serverErrors={serverErrors}
                  formErrors={{}}
                  customErrors={[]}
                />
                <SessionExpiredError showError={sessionExpired} />
              </Col>
            </Row>
            <Row className="pb-2">
              <Col md="5" className="px-0 mt-2 mb-n2">
                <h2 className="dark-purple title">Reset password</h2>
              </Col>
              <Col md="7" className="pt-2 px-0 text-right">
                <SkinnedButton onClick={goBack} className="btn-profile-edit">
                  Go Back
                </SkinnedButton>
                <SkinnedButton
                  color="primary"
                  className="ml-2 btn-profile-edit"
                >
                  Save
                </SkinnedButton>
              </Col>
            </Row>
            <Row>
              <Col md="12" className="rounded px-0 mb-4">
                <Label className="pt-4 regular-font">
                  Please specify the new password
                </Label>
                <Row className="pt-3">
                  <Col md="3">
                    <TextInput
                      maxLength={100}
                      labelName="New Password"
                      requiredField={true}
                      className={classnames({
                        highlighted:
                          isNotUndefined(formErrors.newPassword) ||
                          isNotUndefined(formErrors.NewPassword),
                      })}
                      fieldText={values.newPassword || ""}
                      fieldName="newPassword"
                      handleChange={handleChange}
                      onBlur={handleBlur}
                      isPassword={true}
                      autoComplete="new-password"
                      error={formErrors.newPassword || formErrors.NewPassword}
                    ></TextInput>
                    <TextInput
                      maxLength={100}
                      labelName="Confirm New Password"
                      requiredField={true}
                      className={classnames({
                        highlighted:
                          isNotUndefined(formErrors.confirmNewPassword) ||
                          isNotUndefined(formErrors.ConfirmNewPassword),
                      })}
                      fieldText={values.confirmNewPassword || ""}
                      fieldName="confirmNewPassword"
                      handleChange={handleChange}
                      onBlur={handleBlur}
                      isPassword={true}
                      autoComplete="new-password"
                      error={
                        formErrors.confirmNewPassword ||
                        formErrors.ConfirmNewPassword
                      }
                    ></TextInput>
                  </Col>
                </Row>
              </Col>
            </Row>
          </Form>
        </div>
      </LoadingSection>
    </Container>
  );
};
